import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { JSONContent } from '@tiptap/core';
import {
  useTheme,
  sanitizeRichTextContent,
  MAP_TYPE_LABELS,
  type MapBlock,
  type MapTitleLevel,
  type ThemeContextValue,
} from '@course-authoring/shared-types';
import { useMapVersions } from '../api/mapApi';
import { TitleLevelSelector, getTitleInputStyle } from './TitleLevelSelector';
import { InteractionRichTextField } from './InteractionRichTextField';
import { createTokenRichTextToolbarStyles } from './interactionEditorUtils';

type ThemeTokens = ThemeContextValue['tokens'];

const EMPTY_RICH_TEXT_DOC: JSONContent = {
  type: 'doc',
  content: [{ type: 'paragraph' }],
};

function normalizeDescription(
  description: string | JSONContent
): JSONContent {
  if (typeof description === 'string') {
    if (!description.trim()) {
      return EMPTY_RICH_TEXT_DOC;
    }
    const paragraphs = description
      .split(/\n+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    return sanitizeRichTextContent({
      type: 'doc',
      content: (paragraphs.length > 0 ? paragraphs : ['']).map((p) => ({
        type: 'paragraph',
        ...(p.length > 0 ? { content: [{ type: 'text', text: p }] } : {}),
      })),
    });
  }
  return sanitizeRichTextContent(description);
}

export interface MapBlockEditorProps {
  block: MapBlock;
  onChange: (updates: Partial<MapBlock>) => void;
  autoFocus?: boolean;
  translationMode?: boolean;
}

export function MapBlockEditor({ block, onChange, translationMode = false }: MapBlockEditorProps) {
  const { tokens } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const styles = useMemo(() => createStyles(tokens), [tokens]);
  const toolbarStyles = useMemo(() => createTokenRichTextToolbarStyles(tokens), [tokens]);
  const { data: mapVersions, isLoading } = useMapVersions();
  const [isDescFocused, setIsDescFocused] = useState(false);

  const titleLevel: MapTitleLevel = block.titleLevel ?? 'h2';
  const descContent = useMemo(
    () => normalizeDescription(block.description),
    [block.description]
  );

  const selectedVersion = useMemo(
    () => mapVersions?.find((v) => v.id === block.mapVersionId),
    [mapVersions, block.mapVersionId]
  );

  const handleVersionChange = useCallback(
    (versionId: string) => {
      const version = mapVersions?.find((v) => v.id === versionId);
      if (version) {
        onChange({
          mapVersionId: versionId,
          title: version.title,
          description: version.description,
          interactionMessage: undefined,
          resolvedMapVersion: version,
        });
      } else {
        onChange({ mapVersionId: versionId, resolvedMapVersion: undefined });
      }
    },
    [mapVersions, onChange]
  );

  useEffect(() => {
    if (!translationMode && selectedVersion && !block.resolvedMapVersion) {
      onChange({ resolvedMapVersion: selectedVersion });
    }
  }, [selectedVersion, block.resolvedMapVersion, onChange, translationMode]);

  return (
    <div ref={containerRef} style={styles.container}>
      {!translationMode ? <label style={styles.fieldLabel}>
        Map Version
        <select
          value={block.mapVersionId}
          onChange={(e) => handleVersionChange(e.target.value)}
          style={styles.select}
        >
          <option value="">
            {isLoading ? 'Loading maps...' : '-- Select a map --'}
          </option>
          {mapVersions?.map((v) => (
            <option key={v.id} value={v.id}>
              {v.title} ({MAP_TYPE_LABELS[v.mapType]})
            </option>
          ))}
        </select>
      </label> : null}

      {!translationMode ? <TitleLevelSelector
        currentLevel={titleLevel}
        onSelectLevel={(level) => onChange({ titleLevel: level as MapTitleLevel })}
        tokens={tokens}
      /> : null}

      <label style={styles.fieldLabel}>
        Title
        <input
          type="text"
          value={block.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Block title"
          style={getTitleInputStyle(titleLevel, tokens)}
        />
      </label>

      <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing.xs }}>
        <span style={styles.fieldLabel}>Description</span>
        <InteractionRichTextField
          content={descContent}
          placeholder="Block description"
          isFocused={isDescFocused}
          onFocus={() => setIsDescFocused(true)}
          onBlur={() => setIsDescFocused(false)}
          onContentChange={(content) => onChange({ description: content })}
          autoFocus={false}
          containerRef={containerRef}
          toolbarPreset="full"
          toolbarStyles={toolbarStyles}
        />
      </div>

      {selectedVersion && (
        <div style={styles.preview}>
          <span style={styles.previewLabel}>
            {MAP_TYPE_LABELS[selectedVersion.mapType]} &middot; {selectedVersion.regions.length} regions
          </span>
        </div>
      )}
    </div>
  );
}

function createStyles(tokens: ThemeTokens): Record<string, CSSProperties> {
  return {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
      padding: tokens.spacing.lg,
    },
    fieldLabel: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.xs,
      fontSize: '13px',
      fontWeight: 600,
      color: tokens.text.muted,
    },
    textInput: {
      height: '36px',
      padding: `0 ${tokens.spacing.md}`,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      backgroundColor: tokens.backgrounds.page,
      color: tokens.text.primary,
      fontSize: '14px',
      fontWeight: 400,
      outline: 'none',
      boxSizing: 'border-box',
    },
    select: {
      height: '36px',
      padding: `0 ${tokens.spacing.md}`,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      backgroundColor: tokens.backgrounds.page,
      color: tokens.text.primary,
      fontSize: '14px',
      fontWeight: 400,
      outline: 'none',
    },
    preview: {
      padding: tokens.spacing.md,
      backgroundColor: tokens.backgrounds.surface,
      borderRadius: tokens.radius.sm,
      border: `1px solid ${tokens.borders.default}`,
    },
    previewLabel: {
      fontSize: '13px',
      color: tokens.text.muted,
    },
  };
}
